import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { apiClient, endpoints } from '../../api';
import './AdminLogin.css';

const AdminLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Clear form data when component mounts (after logout)
  useEffect(() => {
    setFormData({
      email: '',
      password: ''
    });
    setErrors({});
    setShowPassword(false);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Make API call to backend admin login endpoint
      const response = await apiClient.post(endpoints.loginAdmin, {
        email: formData.email,
        password: formData.password
      }, {
        timeout: 10000,
        withCredentials: true
      });

      if (response.status === 200) {
        const adminData = {
          id: response.data.admin.id,
          username: response.data.admin.username,
          email: response.data.admin.email,
          role: response.data.admin.role,
          token: response.data.token
        };
        
        // Store admin token and API key for API calls
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminApiKey', 'dev-admin-key');
        localStorage.setItem('ADMIN_API_KEY', 'dev-admin-key');
        
        // Store session data for SessionContext
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userType', 'admin');
        localStorage.setItem('userEmail', adminData.email);
        
        // Set session expiry (8 hours from now)
        const sessionExpiry = Date.now() + (8 * 60 * 60 * 1000);
        localStorage.setItem('sessionExpiry', sessionExpiry.toString());
        localStorage.setItem('lastActivity', Date.now().toString());
        
        onLogin(adminData);
      }
    } catch (error) {
      console.error('Admin login error:', error);
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          setErrors({ general: 'Invalid email or password' });
        } else if (error.response.status === 403) {
          setErrors({ general: 'Access denied. Admin privileges required.' });
        } else if (error.response.status === 500) {
          setErrors({ general: 'Admin system not available. Please contact support.' });
        } else {
          setErrors({ general: error.response.data?.error || 'Login failed. Please try again.' });
        }
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        // Backend not available - show error instead of demo mode
        setErrors({ general: 'Cannot connect to server. Please check your connection and try again.' });
      } else {
        setErrors({ general: 'Login failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <div className="admin-login-logo">
            <img src="/sonickart1.jpg" alt="SonicKart Logo" className="logo-image" />
            <h1>SonicKart Admin Portal</h1>
            <p className="admin-login-tagline">Manage vendors, orders and operations efficiently with SonicKart</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {errors.general && (
            <div className="error-message general">
              <AlertCircle size={16} />
              <span>{errors.general}</span>
            </div>
          )}

          <div className="form-group">
            
            <div className="input-container">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder=" Email Address"
                disabled={isLoading}
              />
            </div>
            {errors.email && (
              <span className="error-text">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
          
            <div className="input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className="error-text">{errors.password}</span>
            )}
          </div>

          <div className="form-options">
            <label className="checkbox-container">
              <input type="checkbox" />
              <span className="checkmark"></span>
              Remember me
            </label>
            <a href="#" className="forgot-password">
              Forgot password?
            </a>
          </div>

          <button
            type="submit"
            className={`admin-login-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Signing in...
              </>
            ) : (
              'Sign In as Admin'
            )}
          </button>
        </form>

        <div className="admin-login-footer">
          <div className="admin-credentials-info">
            <p className="demo-credentials">
              <strong>Admin Credentials:</strong><br/>
              Email: admin@vendorportal.com<br/>
              Password: Admin123!
            </p>
           
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
